import React, { useEffect, useState } from 'react';
import { navigate } from '@gatsbyjs/reach-router';
import Button from '~/components/atom/Button';
import TEST_ID from './index.testid';
import useCountdown from '~/util/useCountdown';
import useUserLookup from '~/hooks/useUserLookup';
import {
  ButtonContainer,
  CardElement,
  UserEmailElement,
  UserPhoneElement,
  OfficeCounter,
  OfficeIcon,
  UserNameContainer,
  EnvelopeIcon,
  UserRoleElement,
} from '../UserCard.style';
import {
  SessionHydrationUserFieldsFragment,
  SessionHydrationUserFields_PendingUser_Fragment,
  useResendUserInvitationMutation,
  UserRole,
  UserStatus,
  useUpdateAccountRelationMutation,
  type SessionHydrationOfficeFieldsFragment,
} from '~/graphql/types';
import { isPendingStatus } from '~/util/constants';
import useUserRelationToAccount from '~/hooks/useUserRelationToAccount';
import useCurrentAccount from '~/hooks/useCurrentAccount';
import useCurrentUser from '~/hooks/useCurrentUser';
import type { SelectedOptionOf } from '~/components/molecule/Dropdown';
import useConfirmModal from '~/hooks/useConfirmModal';
import useUsers from '~/hooks/useUsers';
import useOffices from '~/hooks/useOffices';
import Dropdown from '~/components/molecule/Dropdown';
import { isNil } from 'ramda';
import useUserRights from '~/hooks/useUserRights';
import useSessionHydration from '~/hooks/useSessionHydration';
import useViewingModeProps from '~/hooks/useViewingModeProps';
import useErrorReporter from '~/hooks/useErrorReporter';
import type { ListItemForDeletion } from '../../../components/DeleteEntityModals/components/ListItemCard';
import DeleteUserFromAccountModal from '../../../components/DeleteEntityModals/DeleteUserFromAccount';

const text = {
  sendInvitationAgain: 'Uitnodiging opnieuw versturen',
  user: 'Gebruiker',
  admin: 'Admin',
  save: 'Opslaan',
  confirmRoleChangeTitle: 'Rol wijzigen',
  confirmRoleChangeMsg:
    'Je wijzigt de rol van deze gebruiker binnen dit account. Weet je het zeker?',
  confirmRoleChangeMsgForMe:
    'Je wijzigt je eigen rol binnen dit account. Weet je het zeker?',
};

type Props = {
  userId: string;
  refetch: () => void;
};

const isPending = (
  user,
): user is SessionHydrationUserFields_PendingUser_Fragment =>
  user?.__typename === 'PendingUser';

const AccountUserCard: React.FCC<Props> = ({ userId, refetch }) => {
  const errorReporter = useErrorReporter();

  const viewingModeProps = useViewingModeProps();
  const [showDeleteUserFromAccountModal, setShowDeleteUserFromAccountModal] =
    useState<Array<ListItemForDeletion> | null>(null);

  const handleDeleteUserFromAccountModalAction = (
    officesList: Array<ListItemForDeletion> | null,
  ) => {
    setShowDeleteUserFromAccountModal(officesList);
  };

  const [, refetchSessionHydration] = useSessionHydration();
  const { id: accountId } = useCurrentAccount();
  const me = useCurrentUser();
  const { update: mayUpdate, delete: mayDelete } = useUserRights({
    category: 'Users',
  });

  const userLookup = useUserLookup(['User', 'PendingUser']) as {
    [userId: string]: SessionHydrationUserFieldsFragment;
  };
  const user = userLookup[userId];
  const offices = useOffices({ userIds: [userId] });
  const relation = useUserRelationToAccount({ accountId: accountId, userId });

  let userDetails: {
    name: string;
    phone: string;
    lastInvitationTime: SessionHydrationUserFields_PendingUser_Fragment['lastInvitationTime'];
    dataTestId: string;
  };
  if (isPending(user)) {
    userDetails = {
      name: user?.email,
      phone: '---',
      lastInvitationTime: user?.lastInvitationTime,
      dataTestId: 'user-list-item-inactive',
    };
  } else {
    userDetails = {
      name: user?.name,
      phone: user?.phone,
      lastInvitationTime: undefined,
      dataTestId: 'user-list-item-active',
    };
  }

  const hasPendingStatus = isPending(user) || isPendingStatus(relation?.status);
  const { name, phone, lastInvitationTime } = userDetails;

  const accountAdmins = useUsers({
    statuses: [UserStatus.Active],
    officeIds: [],
  }).filter(user => user.role === UserRole.Admin);

  const lastAccountAdmin = accountAdmins.length === 1 ? accountAdmins[0] : null;
  const userIsLastAccountAdmin =
    accountAdmins.length === 1 && lastAccountAdmin?.id === user?.id;

  const [newRole, setNewRole] = useState<UserRole | null>(null);
  const { setShowModal, modal } = useConfirmModal({
    level: userId === me.id ? 'danger' : 'warning',
    labels: {
      title: text.confirmRoleChangeTitle,
      message:
        user?.id === me?.id
          ? text.confirmRoleChangeMsgForMe
          : text.confirmRoleChangeMsg,
    },
    buttons: [
      {
        label: text.save,
        onClick: () => onRoleChange(userId, newRole),
        dataTestId: 'modal-confirm-button',
      },
    ],
  });

  const [updateAccountRelation, { loading: userRoleLoading }] =
    useUpdateAccountRelationMutation();

  const onRoleChange = (userId: string, newRole: UserRole | null) => {
    if (!newRole) {
      return errorReporter.captureException(
        new Error('newRole does not exist in updateAccountRelation'),
      );
    }

    void updateAccountRelation({
      variables: {
        accountId,
        userId,
        update: { role: newRole },
      },
    }).then(() => refetch());
  };

  const [buttonDisabled, setButtonDisabled] = useState<boolean>(false);
  const [resendUserInvitation] = useResendUserInvitationMutation({
    onCompleted: () => refetchSessionHydration(),
  });

  const [secondsLeft] = useCountdown(lastInvitationTime, 60);

  const buttonLabel = `${text.sendInvitationAgain} ${
    secondsLeft ? `(${secondsLeft})` : ''
  }`;

  useEffect(() => {
    setButtonDisabled(!isNil(secondsLeft));
  }, [secondsLeft]);

  if (!user) return null;

  return (
    <>
      {modal}
      <CardElement
        data-testid="account-user-card"
        $isPending={hasPendingStatus}
        onClick={() => {
          if (userId === me.id) return navigate('/-/settings/profile');
          if (!hasPendingStatus) void navigate(`/-/settings/users/${userId}`);
          return;
        }}
        data-objectid={userId}
      >
        <UserNameContainer data-testid="user-name" {...viewingModeProps}>
          {hasPendingStatus && (
            <EnvelopeIcon name="mail" title="Invited User" />
          )}
          {name}
        </UserNameContainer>
        <UserPhoneElement data-testid="user-phone" {...viewingModeProps}>
          {phone}
        </UserPhoneElement>
        <UserEmailElement data-testid="user-email" {...viewingModeProps}>
          {user?.email}
        </UserEmailElement>

        <ButtonContainer>
          {lastInvitationTime && (
            <Button
              ghost
              disabled={buttonDisabled || !mayUpdate}
              onClick={() => {
                setButtonDisabled(true);
                return resendUserInvitation({
                  variables: {
                    userId,
                    accountId,
                  },
                });
              }}
              data-testid={TEST_ID.RESEND_INVITATION_BUTTON}
              label={buttonLabel}
            />
          )}

          {!lastInvitationTime && (
            <UserRoleElement title="Rol binnen dit account">
              <Dropdown
                disabled={
                  !mayUpdate || userIsLastAccountAdmin || userRoleLoading
                }
                selectedOptionIdx={accountRoleOptions.findIndex(
                  role => role.key === relation?.role,
                )}
                onChange={({ option }: SelectedOptionOf<UserRole>) => {
                  if (!option) return;

                  const newRole = option.payload;
                  if (relation?.role === newRole) return;

                  setNewRole(newRole);
                  setShowModal(true);
                }}
                options={accountRoleOptions}
                dataTestId="account-role-dropdown"
              />
            </UserRoleElement>
          )}

          <OfficeCounter>
            <OfficeIcon name="office" />
            <span data-testid="user-officeNumber">{offices.length}</span>
          </OfficeCounter>

          <Button
            size="medium"
            margin={[null, null, null, 'base']}
            ghost
            icon="trashcan"
            appearance="danger"
            dataTestId={TEST_ID.DELETE_USER_BUTTON}
            data-objectid={`delete-user-from-account-${user.id}`}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();

              handleDeleteUserFromAccountModalAction(
                convertOfficesForModal(offices),
              );
            }}
            disabled={!mayDelete}
          />
        </ButtonContainer>
      </CardElement>

      {showDeleteUserFromAccountModal && (
        <DeleteUserFromAccountModal
          list={showDeleteUserFromAccountModal}
          onCancel={() => handleDeleteUserFromAccountModalAction(null)}
          onClose={() => handleDeleteUserFromAccountModalAction(null)}
          userId={userId}
        />
      )}
    </>
  );
};

type RoleOption = { label: string; key: UserRole; payload: UserRole };
const accountRoleOptions: Array<RoleOption> = [
  {
    label: text.user,
    key: UserRole.User,
    payload: UserRole.User,
  },
  {
    label: text.admin,
    key: UserRole.Admin,
    payload: UserRole.Admin,
  },
];

type ConvertOfficesForModalReturnType = {
  id: string;
  img:
    | {
        medium: string;
      }
    | null
    | undefined;
  name: string;
};
const convertOfficesForModal = (
  offices: Array<SessionHydrationOfficeFieldsFragment>,
): Array<ConvertOfficesForModalReturnType> => {
  const convertedOffices: Array<ConvertOfficesForModalReturnType> = [];

  offices.map(office => {
    const { name, id, img } = office;

    convertedOffices.push({
      id,
      img,
      name,
    });
  });

  return convertedOffices;
};

export default AccountUserCard;
